/**
 * AdminKBManager Component
 * Knowledge Base article management with CRUD operations
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkBreaks from 'remark-breaks';
import type { KBArticle, KBCategory } from '../../services/adminService';
import { kbArticlesApi } from '../../services/adminService';
import useAdminAuth from '../../hooks/useAdminAuth';
import AdminLayout from './AdminLayout';
import Modal from '../ui/Modal';

import { FRAME_REGISTRY } from '../../config/frameRegistry';

// Markdown toolbar button component
interface ToolbarButtonProps {
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({ onClick, title, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className="p-1.5 text-gray-600 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors"
  >
    {children}
  </button>
);

// All available frames (sorted numerically)
const AVAILABLE_FRAMES = FRAME_REGISTRY
  .filter(frame => frame.kbEligible)
  .map(frame => frame.frameId);

type SortOption = 'frame' | 'alpha';

const AdminKBManager: React.FC = () => {
  const { isAdmin, isLoading: authLoading } = useAdminAuth();
  
  const [articles, setArticles] = useState<KBArticle[]>([]);
  const [categories, setCategories] = useState<KBCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>('alpha');
  
  // Modal states
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [editingArticle, setEditingArticle] = useState<KBArticle | null>(null);
  const [deletingArticle, setDeletingArticle] = useState<KBArticle | null>(null);
  const [previewingArticle, setPreviewingArticle] = useState<KBArticle | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    categoryId: '',
    frames: [] as string[],
    tags: [] as string[],
    isPublished: true
  });
  const [newCategory, setNewCategory] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  
  // Dropdown menu state
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  
  // Preview length setting
  const [previewLength, setPreviewLength] = useState<number>(() => {
    const stored = localStorage.getItem('kb-preview-length');
    if (stored) {
      const parsed = parseInt(stored, 10);
      if ([50, 100, 150, 200].includes(parsed)) {
        return parsed;
      }
    }
    return 100; // Default
  });

  const handlePreviewLengthChange = (length: number) => {
    setPreviewLength(length);
    localStorage.setItem('kb-preview-length', length.toString());
  };
  
  // Export/Import state
  const [exportLoading, setExportLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Textarea ref for markdown toolbar
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Insert markdown formatting at cursor position or wrap selection
  const insertMarkdown = (before: string, after: string = '', placeholder: string = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.content.substring(start, end);
    const textToInsert = selectedText || placeholder;
    
    const newContent = 
      formData.content.substring(0, start) + 
      before + textToInsert + after + 
      formData.content.substring(end);
    
    setFormData(prev => ({ ...prev, content: newContent }));
    
    // Restore focus and set cursor position
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + before.length + textToInsert.length + after.length;
      textarea.setSelectionRange(
        selectedText ? newCursorPos : start + before.length,
        selectedText ? newCursorPos : start + before.length + placeholder.length
      );
    }, 0);
  };

  // Markdown toolbar actions
  const toolbarActions = {
    h1: () => insertMarkdown('# ', '\n', 'Heading 1'),
    h2: () => insertMarkdown('## ', '\n', 'Heading 2'),
    h3: () => insertMarkdown('### ', '\n', 'Heading 3'),
    bold: () => insertMarkdown('**', '**', 'bold text'),
    italic: () => insertMarkdown('*', '*', 'italic text'),
    strikethrough: () => insertMarkdown('~~', '~~', 'strikethrough'),
    bulletList: () => insertMarkdown('- ', '\n', 'List item'),
    numberedList: () => insertMarkdown('1. ', '\n', 'List item'),
    link: () => insertMarkdown('[', '](url)', 'link text'),
    quote: () => insertMarkdown('> ', '\n', 'Quote'),
    code: () => insertMarkdown('`', '`', 'code'),
    codeBlock: () => insertMarkdown('```\n', '\n```', 'code block'),
    hr: () => insertMarkdown('\n---\n', '', ''),
    table: () => insertMarkdown('\n| Header 1 | Header 2 |\n|----------|----------|\n| Cell 1   | Cell 2   |\n', '', ''),
  };

  const fetchData = useCallback(async () => {
    if (!isAdmin) return;
    
    try {
      setLoading(true);
      const [articlesData, categoriesData] = await Promise.all([
        kbArticlesApi.getArticles(sortBy),
        kbArticlesApi.getCategories()
      ]);
      setArticles(articlesData);
      setCategories(categoriesData);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch KB data:', err);
      setError('Failed to load KB articles');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, sortBy]);

  useEffect(() => {
    if (!authLoading && isAdmin) {
      fetchData();
    }
  }, [authLoading, isAdmin, fetchData]);

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      categoryId: '',
      frames: [],
      tags: [],
      isPublished: true
    });
    setEditingArticle(null);
    setActionError(null);
  };

  const openAddModal = () => {
    resetForm();
    setShowArticleModal(true);
  };

  const openEditModal = (article: KBArticle) => {
    setEditingArticle(article);
    setFormData({
      title: article.title,
      content: article.content,
      categoryId: article.categoryId || '',
      frames: article.frames || [],
      tags: article.tags || [],
      isPublished: article.isPublished
    });
    setShowArticleModal(true);
    setOpenMenuId(null);
  };

  const openDeleteConfirm = (article: KBArticle) => {
    setDeletingArticle(article);
    setShowDeleteModal(true);
    setOpenMenuId(null);
  };

  const openPreviewModal = (article: KBArticle) => {
    setPreviewingArticle(article);
    setShowPreviewModal(true);
    setOpenMenuId(null);
  };

  const handleSaveArticle = async () => {
    if (!formData.title || !formData.content) {
      setActionError('Title and content are required');
      return;
    }

    setActionLoading(true);
    setActionError(null);

    try {
      if (editingArticle) {
        await kbArticlesApi.updateArticle(editingArticle.id, formData);
      } else {
        await kbArticlesApi.createArticle(formData);
      }
      setShowArticleModal(false);
      resetForm();
      fetchData();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to save article');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteArticle = async () => {
    if (!deletingArticle) return;

    setActionLoading(true);
    setActionError(null);

    try {
      await kbArticlesApi.deleteArticle(deletingArticle.id);
      setShowDeleteModal(false);
      setDeletingArticle(null);
      fetchData();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete article');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;

    setActionLoading(true);
    setActionError(null);

    try {
      await kbArticlesApi.createCategory(newCategory.trim());
      setNewCategory('');
      setShowCategoryModal(false);
      // Refresh categories
      const categoriesData = await kbArticlesApi.getCategories();
      setCategories(categoriesData);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to create category');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleFrame = (frameId: string) => {
    setFormData(prev => ({
      ...prev,
      frames: prev.frames.includes(frameId)
        ? prev.frames.filter(f => f !== frameId)
        : [...prev.frames, frameId]
    }));
  };

  const selectAllFrames = () => {
    setFormData(prev => ({ ...prev, frames: [...AVAILABLE_FRAMES] }));
  };

  const deselectAllFrames = () => {
    setFormData(prev => ({ ...prev, frames: [] }));
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);
      const exportData = await kbArticlesApi.exportAllArticles();
      
      // Create downloadable JSON file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `kb-articles-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log('KB articles exported successfully:', {
        articleCount: exportData.metadata.articleCount,
        categoryCount: exportData.metadata.categoryCount
      });
    } catch (err) {
      console.error('Export error:', err);
      setError(err instanceof Error ? err.message : 'Failed to export KB articles');
    } finally {
      setExportLoading(false);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setImportLoading(true);
      setError(null);

      const fileContent = await file.text();
      const importData = JSON.parse(fileContent);

      // Validate structure
      if (!importData.categories || !importData.articles) {
        throw new Error('Invalid import file: missing categories or articles');
      }

      const result = await kbArticlesApi.importAllArticles({
        categories: importData.categories,
        articles: importData.articles
      });

      // Show success message
      alert(
        `Import completed successfully!\n\n` +
        `Categories: ${result.summary.categories.imported} imported, ${result.summary.categories.skipped} skipped\n` +
        `Articles: ${result.summary.articles.imported} imported, ${result.summary.articles.skipped} skipped`
      );

      // Refresh articles list
      await fetchData();
    } catch (err) {
      console.error('Import error:', err);
      setError(err instanceof Error ? err.message : 'Failed to import KB articles');
    } finally {
      setImportLoading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (authLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">KB Manager</h1>
            <p className="text-gray-600 mt-1">Manage Knowledge Base articles</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Export Button */}
            <button
              onClick={handleExport}
              disabled={exportLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Export all KB articles and categories to JSON"
            >
              {exportLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Exporting...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Export
                </>
              )}
            </button>

            {/* Import Button */}
            <button
              onClick={handleImportClick}
              disabled={importLoading}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Import KB articles and categories from JSON"
            >
              {importLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Importing...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Import
                </>
              )}
            </button>
            
            {/* Hidden file input for import */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleImportFile}
              className="hidden"
            />

            {/* Add Article Button */}
            <button
              onClick={openAddModal}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add Article
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex flex-wrap items-center gap-4 sm:gap-6">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Sort by:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              >
                <option value="alpha">Alphabetically</option>
                <option value="frame">By Frame</option>
              </select>
            </div>
            
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Preview length:</label>
              <select
                value={previewLength}
                onChange={(e) => handlePreviewLengthChange(parseInt(e.target.value, 10))}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              >
                <option value="50">50 chars</option>
                <option value="100">100 chars</option>
                <option value="150">150 chars</option>
                <option value="200">200 chars</option>
              </select>
              <span className="text-xs text-gray-400">(for help boxes)</span>
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error}
          </div>
        )}

        {/* Articles List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto"></div>
            </div>
          ) : articles.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              No KB articles found. Click "Add Article" to create one.
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {articles.map((article) => (
                <div key={article.id} className="p-4 hover:bg-gray-50 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-gray-900 truncate">{article.title}</h3>
                      {!article.isPublished && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                          Draft
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                      {article.categoryName && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-teal-100 text-teal-800">
                          {article.categoryName}
                        </span>
                      )}
                      {/* Frames tooltip */}
                      <div className="relative group">
                        <span className="cursor-help">
                          {article.frames?.length || 0} frames
                        </span>
                        {article.frames && article.frames.length > 0 && (
                          <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-10">
                            <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 max-w-xs">
                              <div className="font-medium mb-1">Assigned Frames:</div>
                              <div className="flex flex-wrap gap-1">
                                {article.frames.map(f => (
                                  <span key={f} className="bg-gray-700 px-1.5 py-0.5 rounded">
                                    {f}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="relative ml-4">
                    <button
                      onClick={() => setOpenMenuId(openMenuId === article.id ? null : article.id)}
                      className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                      </svg>
                    </button>
                    
                    {openMenuId === article.id && (
                      <div className="absolute right-0 mt-2 w-36 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                        <button
                          onClick={() => openPreviewModal(article)}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          Preview
                        </button>
                        <button
                          onClick={() => openEditModal(article)}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Edit
                        </button>
                        <button
                          onClick={() => openDeleteConfirm(article)}
                          className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Article Modal */}
      <Modal
        open={showArticleModal}
        onClose={() => { setShowArticleModal(false); resetForm(); }}
        title={editingArticle ? 'Edit Article' : 'Add Article'}
      >
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              placeholder="Article title"
            />
          </div>

          {/* Category */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">Category</label>
              <button
                type="button"
                onClick={() => setShowCategoryModal(true)}
                className="text-xs text-teal-600 hover:text-teal-700"
              >
                + Add Category
              </button>
            </div>
            <select
              value={formData.categoryId}
              onChange={(e) => setFormData(prev => ({ ...prev, categoryId: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              <option value="">Select category...</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          {/* Content with Markdown Toolbar */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Content * (Markdown supported)</label>
            
            {/* Markdown Toolbar */}
            <div className="flex flex-wrap items-center gap-1 p-2 bg-gray-50 border border-gray-300 border-b-0 rounded-t-lg">
              {/* Headings dropdown */}
              <div className="relative group">
                <ToolbarButton onClick={() => {}} title="Headings">
                  <span className="font-bold text-sm">H</span>
                </ToolbarButton>
                <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg hidden group-hover:block z-10">
                  <button onClick={toolbarActions.h1} className="block w-full px-3 py-1.5 text-left text-sm hover:bg-gray-50">
                    <span className="font-bold text-lg">H1</span> <span className="text-gray-500">Heading 1</span>
                  </button>
                  <button onClick={toolbarActions.h2} className="block w-full px-3 py-1.5 text-left text-sm hover:bg-gray-50">
                    <span className="font-bold">H2</span> <span className="text-gray-500">Heading 2</span>
                  </button>
                  <button onClick={toolbarActions.h3} className="block w-full px-3 py-1.5 text-left text-sm hover:bg-gray-50">
                    <span className="font-bold text-sm">H3</span> <span className="text-gray-500">Heading 3</span>
                  </button>
                </div>
              </div>
              
              <div className="w-px h-5 bg-gray-300 mx-1" />
              
              {/* Text styling */}
              <ToolbarButton onClick={toolbarActions.bold} title="Bold (Ctrl+B)">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 4a1 1 0 011-1h6a4 4 0 014 4 4 4 0 01-4 4H4a1 1 0 01-1-1V4zm1 6h6a2 2 0 100-4H4v4zm0 2v4h7a2 2 0 100-4H4z"/>
                </svg>
              </ToolbarButton>
              <ToolbarButton onClick={toolbarActions.italic} title="Italic (Ctrl+I)">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8 3a1 1 0 011-1h6a1 1 0 110 2h-2.5l-2 12H13a1 1 0 110 2H7a1 1 0 110-2h2.5l2-12H9a1 1 0 01-1-1z"/>
                </svg>
              </ToolbarButton>
              <ToolbarButton onClick={toolbarActions.strikethrough} title="Strikethrough">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 3a7 7 0 00-7 7h2a5 5 0 0110 0h2a7 7 0 00-7-7zM3 11h14v2H3v-2z"/>
                </svg>
              </ToolbarButton>
              
              <div className="w-px h-5 bg-gray-300 mx-1" />
              
              {/* Lists */}
              <ToolbarButton onClick={toolbarActions.bulletList} title="Bullet List">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd"/>
                  <circle cx="2" cy="5" r="1"/>
                  <circle cx="2" cy="10" r="1"/>
                  <circle cx="2" cy="15" r="1"/>
                </svg>
              </ToolbarButton>
              <ToolbarButton onClick={toolbarActions.numberedList} title="Numbered List">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 5a1 1 0 011-1h12a1 1 0 110 2H6a1 1 0 01-1-1zm0 5a1 1 0 011-1h12a1 1 0 110 2H6a1 1 0 01-1-1zm0 5a1 1 0 011-1h12a1 1 0 110 2H6a1 1 0 01-1-1z" clipRule="evenodd"/>
                  <text x="1" y="7" fontSize="6" fontWeight="bold">1</text>
                  <text x="1" y="12" fontSize="6" fontWeight="bold">2</text>
                  <text x="1" y="17" fontSize="6" fontWeight="bold">3</text>
                </svg>
              </ToolbarButton>
              
              <div className="w-px h-5 bg-gray-300 mx-1" />
              
              {/* Links & Media */}
              <ToolbarButton onClick={toolbarActions.link} title="Insert Link">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/>
                </svg>
              </ToolbarButton>
              <ToolbarButton onClick={toolbarActions.quote} title="Quote">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 6a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm4 4a1 1 0 011-1h8a1 1 0 110 2H8a1 1 0 01-1-1zm0 4a1 1 0 011-1h8a1 1 0 110 2H8a1 1 0 01-1-1z" clipRule="evenodd"/>
                </svg>
              </ToolbarButton>
              
              <div className="w-px h-5 bg-gray-300 mx-1" />
              
              {/* Code */}
              <ToolbarButton onClick={toolbarActions.code} title="Inline Code">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"/>
                </svg>
              </ToolbarButton>
              <ToolbarButton onClick={toolbarActions.codeBlock} title="Code Block">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm2 2v8h10V6H5z" clipRule="evenodd"/>
                  <path d="M7 9l2 2-2 2M11 9h2"/>
                </svg>
              </ToolbarButton>
              
              <div className="w-px h-5 bg-gray-300 mx-1" />
              
              {/* Extras */}
              <ToolbarButton onClick={toolbarActions.hr} title="Horizontal Rule">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd"/>
                </svg>
              </ToolbarButton>
              <ToolbarButton onClick={toolbarActions.table} title="Insert Table">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                </svg>
              </ToolbarButton>
            </div>
            
            {/* Textarea */}
            <textarea
              ref={textareaRef}
              value={formData.content}
              onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
              rows={12}
              className="w-full border border-gray-300 rounded-b-lg px-4 py-2 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 font-mono text-sm"
              placeholder="Start writing your article..."
            />
            
            {/* Markdown Formatting Help */}
            <details className="mt-2 text-xs text-gray-600 bg-gray-50 rounded-lg border border-gray-200">
              <summary className="px-3 py-2 cursor-pointer hover:bg-gray-100 rounded-lg font-medium text-gray-700">
                📖 Markdown Formatting Guide (click to expand)
              </summary>
              <div className="px-3 pb-3 pt-1 space-y-3">
                {/* Line Breaks */}
                <div>
                  <h4 className="font-semibold text-gray-700 mb-1">Line Breaks & Paragraphs</h4>
                  <ul className="space-y-1 text-gray-600">
                    <li><code className="bg-gray-200 px-1 rounded">Blank line</code> → New paragraph (with spacing) ✅</li>
                    <li><code className="bg-gray-200 px-1 rounded">Single Enter</code> → Line break (no gap) ✅</li>
                    <li><code className="bg-gray-200 px-1 rounded">&lt;br&gt;</code> or <code className="bg-gray-200 px-1 rounded">&lt;br/&gt;</code> → Explicit line break ✅</li>
                  </ul>
                  <p className="text-xs text-teal-600 mt-1">💡 All line break methods work in our editor!</p>
                </div>
                
                {/* Text Formatting */}
                <div>
                  <h4 className="font-semibold text-gray-700 mb-1">Text Formatting</h4>
                  <ul className="space-y-1 text-gray-600">
                    <li><code className="bg-gray-200 px-1 rounded">**bold**</code> → <strong>bold</strong></li>
                    <li><code className="bg-gray-200 px-1 rounded">*italic*</code> → <em>italic</em></li>
                    <li><code className="bg-gray-200 px-1 rounded">~~strikethrough~~</code> → <s>strikethrough</s></li>
                    <li><code className="bg-gray-200 px-1 rounded">`inline code`</code> → <code className="bg-gray-200 px-1 rounded">inline code</code></li>
                  </ul>
                </div>
                
                {/* Headings */}
                <div>
                  <h4 className="font-semibold text-gray-700 mb-1">Headings</h4>
                  <ul className="space-y-1 text-gray-600">
                    <li><code className="bg-gray-200 px-1 rounded"># Heading 1</code> → Large heading</li>
                    <li><code className="bg-gray-200 px-1 rounded">## Heading 2</code> → Medium heading</li>
                    <li><code className="bg-gray-200 px-1 rounded">### Heading 3</code> → Small heading</li>
                  </ul>
                </div>
                
                {/* Lists */}
                <div>
                  <h4 className="font-semibold text-gray-700 mb-1">Lists</h4>
                  <ul className="space-y-1 text-gray-600">
                    <li><code className="bg-gray-200 px-1 rounded">- Item</code> or <code className="bg-gray-200 px-1 rounded">* Item</code> → Bullet list</li>
                    <li><code className="bg-gray-200 px-1 rounded">1. Item</code> → Numbered list</li>
                    <li>Indent with 2 spaces for nested lists</li>
                  </ul>
                </div>
                
                {/* Links & Images */}
                <div>
                  <h4 className="font-semibold text-gray-700 mb-1">Links</h4>
                  <ul className="space-y-1 text-gray-600">
                    <li><code className="bg-gray-200 px-1 rounded">[Link Text](https://url.com)</code> → Clickable link</li>
                    <li><code className="bg-gray-200 px-1 rounded">https://url.com</code> → Auto-linked URL</li>
                  </ul>
                </div>
                
                {/* Other */}
                <div>
                  <h4 className="font-semibold text-gray-700 mb-1">Other Elements</h4>
                  <ul className="space-y-1 text-gray-600">
                    <li><code className="bg-gray-200 px-1 rounded">&gt; Quote text</code> → Blockquote</li>
                    <li><code className="bg-gray-200 px-1 rounded">---</code> → Horizontal line</li>
                    <li><code className="bg-gray-200 px-1 rounded">```code block```</code> → Multi-line code</li>
                  </ul>
                </div>
                
                {/* Tips */}
                <div className="pt-2 border-t border-gray-200">
                  <h4 className="font-semibold text-gray-700 mb-1">💡 Tips</h4>
                  <ul className="space-y-1 text-gray-600">
                    <li>• Select text then click toolbar button to wrap it</li>
                    <li>• Use Preview (if available) to check formatting</li>
                    <li>• Empty lines separate paragraphs</li>
                    <li>• For tables, use the table button in the toolbar</li>
                  </ul>
                </div>
              </div>
            </details>
          </div>

          {/* Frame Selection */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Assign to Frames ({formData.frames.length} selected)
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={selectAllFrames}
                  className="text-xs text-teal-600 hover:text-teal-700"
                >
                  Select All
                </button>
                <span className="text-gray-300">|</span>
                <button
                  type="button"
                  onClick={deselectAllFrames}
                  className="text-xs text-teal-600 hover:text-teal-700"
                >
                  Deselect All
                </button>
              </div>
            </div>
            <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto">
              <div className="grid grid-cols-6 gap-2">
                {AVAILABLE_FRAMES.map(frameId => (
                  <label
                    key={frameId}
                    className={`flex items-center gap-1.5 p-1.5 rounded cursor-pointer text-sm ${
                      formData.frames.includes(frameId)
                        ? 'bg-teal-100 text-teal-800'
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.frames.includes(frameId)}
                      onChange={() => toggleFrame(frameId)}
                      className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                    />
                    <span>{frameId}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Published */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isPublished"
              checked={formData.isPublished}
              onChange={(e) => setFormData(prev => ({ ...prev, isPublished: e.target.checked }))}
              className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
            />
            <label htmlFor="isPublished" className="text-sm text-gray-700">Published</label>
          </div>

          {actionError && (
            <p className="text-red-600 text-sm">{actionError}</p>
          )}

          <div className="flex gap-3 justify-end pt-4 border-t">
            <button
              onClick={() => { setShowArticleModal(false); resetForm(); }}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveArticle}
              disabled={actionLoading}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
            >
              {actionLoading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDeletingArticle(null); }}
        title="Delete Article"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Are you sure you want to delete "<strong>{deletingArticle?.title}</strong>"?
            This action cannot be undone.
          </p>
          {actionError && (
            <p className="text-red-600 text-sm">{actionError}</p>
          )}
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => { setShowDeleteModal(false); setDeletingArticle(null); }}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteArticle}
              disabled={actionLoading}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {actionLoading ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Add Category Modal */}
      <Modal
        open={showCategoryModal}
        onClose={() => { setShowCategoryModal(false); setNewCategory(''); setActionError(null); }}
        title="Add Category"
      >
        <div className="space-y-4">
          <input
            type="text"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="Category name"
          />
          {actionError && (
            <p className="text-red-600 text-sm">{actionError}</p>
          )}
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => { setShowCategoryModal(false); setNewCategory(''); setActionError(null); }}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAddCategory}
              disabled={actionLoading || !newCategory.trim()}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
            >
              {actionLoading ? 'Adding...' : 'Add'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Preview Modal */}
      <Modal
        open={showPreviewModal}
        onClose={() => { setShowPreviewModal(false); setPreviewingArticle(null); }}
        title="Article Preview"
      >
        {previewingArticle && (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            {/* Article Title */}
            <div className="border-b border-gray-200 pb-4">
              <h2 className="text-xl font-bold text-gray-900">{previewingArticle.title}</h2>
              {previewingArticle.categoryName && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-teal-100 text-teal-800 mt-2">
                  {previewingArticle.categoryName}
                </span>
              )}
            </div>
            
            {/* Article Content - Rendered Markdown */}
            <div className="text-gray-700 [&_h1]:text-gray-900 [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mt-4 [&_h1]:mb-2 [&_h2]:text-gray-900 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-2 [&_h3]:text-gray-900 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1 [&_p]:my-2 [&_ul]:list-disc [&_ul]:ml-5 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:ml-5 [&_ol]:my-2 [&_li]:my-1 [&_a]:text-teal-600 [&_a]:underline [&_strong]:font-bold [&_strong]:text-gray-900 [&_em]:italic [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm [&_pre]:bg-gray-100 [&_pre]:p-3 [&_pre]:rounded [&_pre]:overflow-x-auto [&_blockquote]:border-l-4 [&_blockquote]:border-teal-500 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:bg-gray-50 [&_blockquote]:py-2 [&_hr]:my-4 [&_hr]:border-gray-300">
              <ReactMarkdown
                remarkPlugins={[remarkBreaks]}
                rehypePlugins={[rehypeRaw]}
              >
                {previewingArticle.content || ''}
              </ReactMarkdown>
            </div>
            
            {/* Assigned Frames Info */}
            {previewingArticle.frames && previewingArticle.frames.length > 0 && (
              <div className="border-t border-gray-200 pt-4">
                <p className="text-xs text-gray-500 mb-2">This article appears on frames:</p>
                <div className="flex flex-wrap gap-1">
                  {previewingArticle.frames.map(f => (
                    <span key={f} className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs">
                      {f}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Close Button */}
            <div className="flex justify-end pt-4 border-t">
              <button
                onClick={() => { setShowPreviewModal(false); setPreviewingArticle(null); }}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Click outside to close menu */}
      {openMenuId && (
        <div 
          className="fixed inset-0 z-0" 
          onClick={() => setOpenMenuId(null)}
        />
      )}
    </AdminLayout>
  );
};

export default AdminKBManager;

